import type { ResponsiveSectionHeight, SectionStyle, SiteSection, ViewportKey } from '../types';
import { placementFor, layoutFromColumn } from '../grid';
import { inferLayout } from '../layouts';

export const DEFAULT_SECTION_HEIGHT = 560;
export const LEGACY_SECTION_BOTTOM_SPACING = 48;

const LEGACY_HEIGHTS: Record<NonNullable<SectionStyle['minHeight']>, number> = {
  auto: DEFAULT_SECTION_HEIGHT,
  medium: 560,
  tall: 720,
  screen: 800,
};

export function legacySectionHeight(style?: SectionStyle): number {
  return LEGACY_HEIGHTS[style?.minHeight ?? 'auto'];
}

export function sectionHeightFor(height: ResponsiveSectionHeight | undefined, viewport: ViewportKey, style?: SectionStyle): number {
  const base = height?.desktop ?? legacySectionHeight(style);
  return viewport === 'tablet' ? height?.tablet ?? base : viewport === 'mobile' ? height?.mobile ?? height?.tablet ?? base : base;
}

export function resolveSectionGeometry(section: Pick<SiteSection, 'style'>, viewport: ViewportKey) {
  const height = sectionHeightFor(section.style?.height, viewport, section.style);
  return { height, minHeight: section.style?.minHeight ?? 'auto', contentHeight: height };
}

export function shouldCollapseEmptySection(
  sectionIsEmpty: boolean,
  hasExplicitHeight: boolean,
  isLastSection: boolean,
  connectToFooter: boolean,
): boolean {
  return sectionIsEmpty && (!hasExplicitHeight || (isLastSection && connectToFooter));
}

export function materializeSectionHeight(style?: SectionStyle, section?: Pick<SiteSection, 'columns' | 'columnLayout'>): ResponsiveSectionHeight {
  const minHeight = legacySectionHeight(style);
  if (!section) return { desktop: minHeight };
  const layout = section.columnLayout ?? inferLayout(section.columns.length);
  const maxChildBottom = section.columns.reduce((max, column, columnIndex) => column.blocks.reduce((columnMax, block) => {
    const placement = placementFor(block.layout ?? layoutFromColumn(layout, columnIndex), 'desktop');
    const fallbackHeight = block.type === 'shape' ? 180 : 96;
    return Math.max(columnMax, placement.offsetY + (placement.height ?? fallbackHeight));
  }, max), 0);
  return { desktop: Math.max(minHeight, maxChildBottom + LEGACY_SECTION_BOTTOM_SPACING) };
}
